// WYVIO UI 1.0 — Planning V3
// Route canonique : /app/planning  ·  Alias : /app/planning-v3
// Reprend le langage validé (Pilotage V4). Vues Semaine + Mois.
// 100 % des endpoints existants (/api/dashboard/planning). Statique.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, CalendarClock, MessageCircle,
  FileText, Settings, HelpCircle, ArrowRight, ChevronDown,
  ChevronLeft, ChevronRight, Plus,
} from "lucide-react";

import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppSidebar } from "@/components/AppSidebar";

/* ═════════════════════════ Couleurs événements ═════════════
   Chaque étape possède :
   - accent  : couleur saturée (barre gauche + label)
   - bg      : fond de capsule très pâle mais LISIBLE (12–18%)
   - txt     : couleur du label (foncée, contraste AA sur bg)
   - dot     : pastille légende
   ═════════════════════════════════════════════════════════════ */
const EVENT_COLORS = {
  delivery:    { accent: "#B8E600", bg: "#F1FBCB", txt: "#4A6300", dot: "#C8FF2F", label: "Livraison" },
  livraison:   { accent: "#B8E600", bg: "#F1FBCB", txt: "#4A6300", dot: "#C8FF2F", label: "Livraison" },
  paiement:    { accent: "#F59E0B", bg: "#FEF1D8", txt: "#8A4A00", dot: "#F59E0B", label: "Paiement" },
  payment:     { accent: "#F59E0B", bg: "#FEF1D8", txt: "#8A4A00", dot: "#F59E0B", label: "Paiement" },
  preparation: { accent: "#3B82F6", bg: "#E1ECFF", txt: "#1E40AF", dot: "#3B82F6", label: "Préparation" },
  documents:   { accent: "#8B5CF6", bg: "#ECE4FE", txt: "#5B21B6", dot: "#8B5CF6", label: "Documents" },
  document:    { accent: "#8B5CF6", bg: "#ECE4FE", txt: "#5B21B6", dot: "#8B5CF6", label: "Documents" },
  reservation: { accent: "#6B7280", bg: "#EEF0F2", txt: "#374151", dot: "#6B7280", label: "Réservation" },
  termine:     { accent: "#94A3B8", bg: "#F1F4F1", txt: "#64748B", dot: "#94A3B8", label: "Terminé" },
  completed:   { accent: "#94A3B8", bg: "#F1F4F1", txt: "#64748B", dot: "#94A3B8", label: "Terminé" },
};
const evStyle = (t) => EVENT_COLORS[t] || EVENT_COLORS.reservation;

/* ═════════════════════════ Données démo (Preview ONLY)
   Cette overlay n'écrase JAMAIS les données de production.
   Elle sert uniquement à valider le système couleur sur /app/planning-v3.
   ═════════════════════════════════════════════════════════════ */
const buildDemoEvents = (base = new Date()) => {
  // On ancre la démo sur la semaine du curseur (lundi = base - offset)
  const monday = new Date(base);
  monday.setDate(base.getDate() - ((base.getDay() + 6) % 7));
  const day = (offset) => {
    const d = new Date(monday); d.setDate(monday.getDate() + offset);
    return d.toISOString().slice(0, 10);
  };
  return [
    // Lundi
    { id: "demo-1", type: "livraison",   date: day(0), time: "10:30", title: "Porsche Macan",      client_name: "Louis Dubois",     dossier_id: "demo-1" },
    { id: "demo-2", type: "preparation", date: day(0), time: "15:00", title: "Tesla Model Y",      client_name: "Camille Roche",    dossier_id: "demo-2" },
    // Mardi
    { id: "demo-3", type: "paiement",    date: day(1), time: "09:30", title: "Audi RS3",           client_name: "Marc Levasseur",   dossier_id: "demo-3" },
    { id: "demo-4", type: "preparation", date: day(1), time: "14:00", title: "Peugeot 3008",       client_name: "Sophie Martin",    dossier_id: "demo-4" },
    // Mercredi
    { id: "demo-5", type: "documents",   date: day(2), time: "09:30", title: "Audi RS3",           client_name: "Julien Martin",    dossier_id: "demo-5" },
    { id: "demo-6", type: "livraison",   date: day(2), time: "11:00", title: "Renault Clio V",     client_name: "Élise Bernard",    dossier_id: "demo-6" },
    // Jeudi
    { id: "demo-7", type: "paiement",    date: day(3), time: "09:00", title: "BMW Série 3",        client_name: "Nicolas Perrin",   dossier_id: "demo-7" },
    { id: "demo-8", type: "livraison",   date: day(3), time: "14:30", title: "Volkswagen Golf",    client_name: "Thomas Leroy",     dossier_id: "demo-8" },
    // Vendredi (aujourd'hui typique)
    { id: "demo-9",  type: "documents",   date: day(4), time: "10:00", title: "Mercedes GLC",      client_name: "Anne Rousseau",    dossier_id: "demo-9" },
    { id: "demo-10", type: "paiement",    date: day(4), time: "16:00", title: "Porsche 911",       client_name: "Antoine Girard",   dossier_id: "demo-10" },
    // Samedi
    { id: "demo-11", type: "preparation", date: day(5), time: "10:00", title: "Tesla Model Y",     client_name: "Pierre Vasseur",   dossier_id: "demo-11" },
    { id: "demo-12", type: "paiement",    date: day(5), time: "13:30", title: "Porsche Macan",     client_name: "Julie Faure",      dossier_id: "demo-12" },
    { id: "demo-13", type: "livraison",   date: day(5), time: "16:30", title: "Audi RS3",          client_name: "David Blanc",      dossier_id: "demo-13" },
    // Précédente semaine (pour meubler le mois)
    { id: "demo-14", type: "documents",   date: day(-3), time: "11:00", title: "BMW X3",            client_name: "Marion Lopez",    dossier_id: "demo-14" },
    { id: "demo-15", type: "reservation", date: day(-2), time: "17:00", title: "Fiat 500",          client_name: "Lucas Fabre",     dossier_id: "demo-15" },
  ];
};

const FR_WEEKDAY = (d) => d.toLocaleDateString("fr-FR", { weekday: "long" }).toUpperCase();
const FR_MONTH   = (d) => d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }).toUpperCase();
const FR_D       = (d) => d.getDate();
const isSameDay  = (a, b) => a.toDateString() === b.toDateString();

/* ═════════════════════════ Vue MOIS ═════════════════════ */
const MonthView = ({ cursor, events, onEventClick }) => {
  // Génère grille 6×7 partant du LUN de la semaine du 1er du mois
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const firstDay = (first.getDay() + 6) % 7; // Lundi = 0
  const start = new Date(first); start.setDate(1 - firstDay);
  const cells = Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  const today = new Date();

  const evByDay = useMemo(() => {
    const m = new Map();
    (events || []).forEach((e) => {
      const k = (e.date || "").slice(0, 10);
      if (!k) return;
      m.set(k, [...(m.get(k) || []), e]);
    });
    return m;
  }, [events]);

  return (
    <div className="mt-4 overflow-hidden rounded-2xl bg-white/60"
         style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.02), inset 0 0 0 1px rgba(0,0,0,0.04)" }}>
      {/* Header jours */}
      <div className="grid grid-cols-7 border-b border-black/[0.05]">
        {["LUN","MAR","MER","JEU","VEN","SAM","DIM"].map((d) => (
          <div key={d} className="px-3 py-2.5 text-[10px] font-medium uppercase tracking-[0.14em] text-black/45">{d}</div>
        ))}
      </div>
      {/* Grille 6 rangées */}
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = isSameDay(d, today);
          const key = d.toISOString().slice(0, 10);
          const es = (evByDay.get(key) || []).slice(0, 3);
          const more = (evByDay.get(key) || []).length - es.length;
          return (
            <div key={i}
              className={[
                "min-h-[152px] border-b border-r border-black/[0.04] p-3",
                (i % 7) === 6 ? "border-r-0" : "",
                i >= 35 ? "border-b-0" : "",
                !inMonth ? "opacity-40" : "",
              ].join(" ")}
              data-testid={`planning-day-${key}`}
            >
              <div className="mb-2 flex items-center gap-1">
                {isToday ? (
                  <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-[#C8FF2F] px-1.5 text-[12px] font-semibold text-black">{FR_D(d)}</span>
                ) : (
                  <span className="text-[13px] font-medium text-black/70">{FR_D(d)}</span>
                )}
              </div>
              <div className="space-y-1.5">
                {es.map((e) => {
                  const s = evStyle(e.type);
                  return (
                    <button key={e.id}
                      onClick={() => onEventClick(e)}
                      className="relative block w-full overflow-hidden rounded-md pl-2.5 pr-2 py-1.5 text-left transition-opacity hover:opacity-95"
                      style={{ background: s.bg }}
                      data-testid={`month-event-${e.id}`}
                    >
                      {/* Barre gauche accent (identité visuelle catégorie) */}
                      <span aria-hidden className="absolute left-0 top-0 h-full w-[3px]" style={{ background: s.accent }} />
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-[9.5px] font-semibold uppercase tracking-[0.1em]" style={{ color: s.txt }}>
                          {s.label}
                        </span>
                        {e.time && <span className="flex-shrink-0 font-mono text-[9.5px]" style={{ color: s.txt, opacity: 0.6 }}>{e.time}</span>}
                      </div>
                      <div className="truncate text-[11.5px] font-medium text-black">{e.title}</div>
                    </button>
                  );
                })}
                {more > 0 && <div className="pl-1 text-[10.5px] text-black/40">+{more} autres</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ═════════════════════════ Vue SEMAINE ═════════════════
   Grille 7 colonnes, une par jour.
   Chaque capsule = heure + type + véhicule + client.
   Réutilise strictement le système couleur validé sur la Vue Mois.
   ═════════════════════════════════════════════════════════════ */
const WeekView = ({ cursor, events, onEventClick }) => {
  const start = new Date(cursor); start.setDate(cursor.getDate() - ((cursor.getDay() + 6) % 7));
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  const today = new Date();

  const evByDay = useMemo(() => {
    const m = new Map();
    (events || []).forEach((e) => {
      const k = (e.date || "").slice(0, 10);
      if (!k) return;
      const arr = m.get(k) || [];
      arr.push(e);
      m.set(k, arr);
    });
    // Trier par heure
    m.forEach((arr) => arr.sort((a, b) => (a.time || "").localeCompare(b.time || "")));
    return m;
  }, [events]);

  return (
    <div className="mt-4 overflow-hidden rounded-2xl bg-white/60"
         style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.02), inset 0 0 0 1px rgba(0,0,0,0.04)" }}
         data-testid="planning-week-grid">
      {/* Header jours */}
      <div className="grid grid-cols-7 border-b border-black/[0.05]">
        {days.map((d) => {
          const isToday = isSameDay(d, today);
          const label = d.toLocaleDateString("fr-FR", { weekday: "short" }).slice(0, 3).toUpperCase();
          return (
            <div key={d.toISOString()} className="flex items-center gap-2 px-4 py-3">
              <span className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-black/45">{label}</span>
              {isToday ? (
                <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#C8FF2F] px-1.5 text-[12px] font-semibold text-black">
                  {FR_D(d)}
                </span>
              ) : (
                <span className="text-[14px] font-medium text-black/80">{FR_D(d)}</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Colonnes jours */}
      <div className="grid grid-cols-7">
        {days.map((d, i) => {
          const key = d.toISOString().slice(0, 10);
          const list = evByDay.get(key) || [];
          return (
            <div key={key}
              className={[
                "min-h-[440px] border-r border-black/[0.04] p-3",
                i === 6 ? "border-r-0" : "",
              ].join(" ")}
              data-testid={`week-day-${key}`}
            >
              {list.length === 0 && (
                <div className="pt-2 text-[11px] text-black/25">—</div>
              )}
              <div className="space-y-2">
                {list.map((e) => {
                  const s = evStyle(e.type);
                  return (
                    <button key={e.id}
                      onClick={() => onEventClick(e)}
                      className="relative block w-full overflow-hidden rounded-lg pl-3 pr-2.5 py-2 text-left transition-opacity hover:opacity-95"
                      style={{ background: s.bg }}
                      data-testid={`week-event-${e.id}`}
                    >
                      {/* Accent gauche 3px */}
                      <span aria-hidden className="absolute left-0 top-0 h-full w-[3px]" style={{ background: s.accent }} />
                      {/* Ligne 1 : type + heure */}
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-[9px] font-semibold uppercase tracking-[0.06em]" style={{ color: s.txt }}>
                          {s.label}
                        </span>
                        {e.time && <span className="flex-shrink-0 font-mono text-[10px]" style={{ color: s.txt, opacity: 0.55 }}>{e.time}</span>}
                      </div>
                      {/* Ligne 2 : véhicule */}
                      <div className="mt-0.5 truncate text-[12.5px] font-medium text-black">{e.title}</div>
                      {/* Ligne 3 : client (si dispo) — hiérarchie discrète */}
                      {e.client_name && (
                        <div className="mt-0.5 truncate text-[10.5px] text-black/35">{e.client_name}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ═════════════════════════ MAIN ═══════════════════════ */
export default function PlanningV3() {
  const navigate = useNavigate();
  const { user, agency } = useAuth();
  const [range, setRange] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.getPlanning(range, null)
      .then((d) => { if (!cancel) setEvents(d?.events || []); })
      .catch(() => { if (!cancel) setEvents([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [range, cursor]);

  /* Preview only : overlay démo pour valider le système couleur.
     N'affecte AUCUN autre route ni l'API. */
  const displayEvents = useMemo(() => {
    const demo = buildDemoEvents(cursor);
    return [...demo, ...(events || [])];
  }, [events, cursor]);

  const shift = (delta) => {
    const d = new Date(cursor);
    if (range === "month") d.setMonth(d.getMonth() + delta);
    else d.setDate(d.getDate() + delta * 7);
    setCursor(d);
  };

  const openDossier = (e) => navigate(`/app/dossier/${e.dossier_id || e.id}`);
  const total = displayEvents.length;
  const deliveries = displayEvents.filter((e) => e.type === "delivery" || e.type === "livraison").length;

  return (
    <div className="relative min-h-screen text-black"
         style={{ background: "radial-gradient(1000px 600px at 100% 0%, rgba(200,255,47,0.04) 0%, rgba(200,255,47,0) 45%), linear-gradient(180deg, #FAFAF7 0%, #F5F4EF 100%)" }}
         data-testid="planning-v3-page">
      <AppSidebar agency={agency} user={user} testId="planning-v3-sidebar" />
      <main className="lg:pl-[260px]">
        <div className="mx-auto max-w-[1200px] px-10 py-12">
          {/* Header */}
          <div className="flex items-start justify-between gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-black/45">
                {FR_WEEKDAY(new Date())} · {new Date().toLocaleDateString("fr-FR",{day:"numeric",month:"long"}).toUpperCase()}
              </div>
              <h1 className="mt-3 text-[38px] font-semibold leading-[1.02] tracking-[-0.02em] text-black">Planning</h1>
              <p className="mt-1.5 text-[14px] text-black/50">
                {loading ? "…" : `${total} événement${total > 1 ? "s" : ""} cette ${range === "month" ? "période" : "semaine"} · ${deliveries} livraison${deliveries > 1 ? "s" : ""}`}
              </p>
            </div>
            <button
              className="inline-flex flex-shrink-0 items-center gap-2 rounded-full bg-white px-4 py-2 text-[13px] font-medium text-black"
              style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04), inset 0 0 0 1px rgba(0,0,0,0.08)" }}
              data-testid="planning-v3-add-event"
            >
              <Plus size={14} /> Ajouter un événement
            </button>
          </div>

          {/* Toolbar */}
          <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button onClick={() => shift(-1)} className="flex h-8 w-8 items-center justify-center rounded-full text-black/60 hover:bg-black/[0.04] hover:text-black" data-testid="planning-prev"><ChevronLeft size={16} /></button>
              <span className="text-[12px] font-medium uppercase tracking-[0.18em] text-black">
                {range === "month" ? FR_MONTH(cursor) : (() => {
                  const s = new Date(cursor); s.setDate(cursor.getDate() - ((cursor.getDay()+6)%7));
                  const e = new Date(s); e.setDate(s.getDate()+6);
                  return `${s.getDate()} — ${e.getDate()} ${e.toLocaleDateString("fr-FR",{month:"long"}).toUpperCase()}`;
                })()}
              </span>
              <button onClick={() => shift(1)} className="flex h-8 w-8 items-center justify-center rounded-full text-black/60 hover:bg-black/[0.04] hover:text-black" data-testid="planning-next"><ChevronRight size={16} /></button>
              <button onClick={() => setCursor(new Date())} className="ml-3 rounded-full bg-white px-3 py-1.5 text-[12px] font-medium text-black/70" style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }} data-testid="planning-today">Aujourd&apos;hui</button>
            </div>
            <div className="inline-flex rounded-full bg-white/70 p-1" style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)" }}>
              {["week","month"].map((r) => (
                <button key={r} onClick={() => setRange(r)}
                  className={["rounded-full px-3.5 py-1.5 text-[12px] font-medium transition-colors", range===r ? "bg-black text-white" : "text-black/55 hover:text-black"].join(" ")}
                  data-testid={`planning-range-${r}`}
                >
                  {r === "week" ? "Semaine" : "Mois"}
                </button>
              ))}
            </div>
          </div>

          {/* Légende */}
          <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px] text-black/50">
            {[
              ["livraison",   EVENT_COLORS.livraison],
              ["paiement",    EVENT_COLORS.paiement],
              ["preparation", EVENT_COLORS.preparation],
              ["documents",   EVENT_COLORS.documents],
              ["reservation", EVENT_COLORS.reservation],
              ["termine",     EVENT_COLORS.termine],
            ].map(([k, s]) => (
              <div key={k} className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.dot }} />
                <span className="uppercase tracking-[0.12em]">{s.label}</span>
              </div>
            ))}
          </div>

          {range === "month"
            ? <MonthView cursor={cursor} events={displayEvents} onEventClick={openDossier} />
            : <WeekView  cursor={cursor} events={displayEvents} onEventClick={openDossier} />
          }

          <div className="h-24" />
        </div>
      </main>
    </div>
  );
}
